import { Auth } from "aws-amplify"

import parseJwt from "@helpers/parseJwt"
import openNewTab from "@helpers/openNewTab"
import { HOST, ORGANIZATION_STORAGE_KEY, isBackstage as isBackstagePortal } from "@components/constants"

import request from "./request"

export const KEY_AUTHENTICATION = "Authentication"
const AUTHENTICATE_DEV_URL = `${HOST}/api/identity/Authenticate`

const IMPERSONATED_STORAGE_KEY = 'impersonatedAuthentication'

export const ERROR_UNAUTHORIZED = "UnauthorizedError"


const getIdToken = async () => {
  let user = {}

  try {
    user = await Auth.currentAuthenticatedUser()
  } catch (error) {
    const isExpiredTokenError = error.message === 'jwt expired'

    if (isExpiredTokenError) {
      resetSessionStorage()

      return window.location.reload()
    }

    throw error
  }

  const { signInUserSession } = user
  const { jwtToken } = signInUserSession.idToken

  return jwtToken
}


export const getStorageKey = isBackstage => {
  const storageKey = `${isBackstage ? "backstage" : "investor"}${KEY_AUTHENTICATION}`
  return storageKey
}


const authenticate = async (onAuthenticationError, isBackstage, shouldRefresh = false) => {
  if (!isBackstage) {
    const impersonatedAuthentication = window.localStorage.getItem(IMPERSONATED_STORAGE_KEY)
    window.localStorage.removeItem(IMPERSONATED_STORAGE_KEY)

    if (impersonatedAuthentication) {
      const investorStorageKey = getStorageKey(false)
      window.sessionStorage.setItem(investorStorageKey, impersonatedAuthentication)

      // eslint-disable-next-line no-console
      console.log('Authenticated as impersonated user')

      return impersonatedAuthentication
    }
  }

  const storageKey = getStorageKey(isBackstage)

  let authentication

  if (!shouldRefresh) {
    authentication = window.sessionStorage.getItem(storageKey)
    if (authentication) { return authentication }
  }

  const idToken = await getIdToken()
  const hasNoAuthenticationParameter = !idToken

  if (hasNoAuthenticationParameter) {
    return null
  }

  const redirectUrl = isBackstage
    ? `${HOST}/backstage/`
    : `${HOST}/`

  const mutation = {
    idToken,
    redirectUrl
  }

  let authenticationJwt

  try {
    const { data } = await request(AUTHENTICATE_DEV_URL, { mutation })
    authenticationJwt = data.authenticationJwt

  } catch (error) {
    onAuthenticationError(error.originalError?.message || error.message)
    return null

  }

  window.sessionStorage.setItem(storageKey, authenticationJwt)
  delete window.isPendingAuthentication

  return authenticationJwt
}


export const setIsPendingAuthentication = () => {
  window.isPendingAuthentication = true
}


export const isPendingAuthentication = () => {
  return !!window.isPendingAuthentication
}


export const signInAsInvestor = (url, authenticationJwt) => {
  localStorage.setItem(IMPERSONATED_STORAGE_KEY, authenticationJwt)
  return openNewTab(url)
}


export const getAuthentication = isBackstage => {
  const storageKey = getStorageKey(isBackstage)
  const authentication = window.sessionStorage.getItem(storageKey)

  return authentication
}


const resetSessionStorage = () => {
  const investorStorageKey = getStorageKey(false)
  sessionStorage.removeItem(investorStorageKey)

  const backstageStorageKey = getStorageKey(true)
  sessionStorage.removeItem(backstageStorageKey)

  sessionStorage.removeItem(ORGANIZATION_STORAGE_KEY)

  localStorage.removeItem(IMPERSONATED_STORAGE_KEY)
}


export const signOut = () => {
  resetSessionStorage()

  return Auth.signOut()
}


export const isImpersonated = () => {
  const storageKey = getStorageKey(isBackstagePortal)

  const authenticationJwt = sessionStorage.getItem(storageKey)

  window.authenticationClaims = parseJwt(authenticationJwt)

  return window.authenticationClaims.isImpersonated
}

export const getIdentityEmail = () => {
  const storageKey = getStorageKey(isBackstagePortal)
  const authenticationJwt = sessionStorage.getItem(storageKey)

  const authenticationPayload = parseJwt(authenticationJwt)
  const { email } = authenticationPayload

  return email
}

export default authenticate
